import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import StickyTable from "../../components/common/StickyTable";
import {
  LEDGER_REPORTS_LIST,
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
} from "../../routes/routes";
import { PAGINATE, NOPAGINATE } from "../../constant/constant";
import {
  NoDataFound,
  Pagination,
  hasRole,
  checkEmptyOrNull,
} from "../../components/Utils";
import { usePermission } from "../../hooks/usePermission";
import Select from "react-select";
import { toast } from "react-toastify";
import moment from "moment";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import {
  EXCEL_SIDEBAR_COLOR,
  styleExcelHeaderRow,
  styleExcelSummaryRow,
  styleExcelTitleRow,
} from "../../utils/excelStyles";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_red.css";
import Loader from "../../components/common/Loader";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { pdfHeaderStyles } from "../../constant/theme";

const LedgerReportsList = () => {
  const { hasPermission } = usePermission();

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({
    total_income: 0,
    total_expense: 0,
    net_balance: 0,
  });
  const [paginator, setPaginator] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [filter, setFilter] = useState({
    country_id: null,
    state_id: null,
    region_id: null,
    chapter_id: null,
    transaction_type: "",
    from_date: "",
    to_date: "",
  });
  const applyFilter = (next) => {
    setPage(1);
    setFilter(next);
  };

  const [btnExcelDisabled, setBtnExcelDisabled] = useState(false);
  const [btnPdfDisabled, setBtnPdfDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [countryOptions, setCountryOptions] = useState({});
  const [stateOptions, setStateOptions] = useState({});
  const [regionOptions, setRegionOptions] = useState({});
  const [chapterOptions, setChapterOptions] = useState({});
  const [showClearBtn, setShowClearBtn] = useState(false);
  const refFlatpickr = useRef(null);

  const selectStateRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);

  const transactionTypeOptions = [
    { value: "1", label: "Income" },
    { value: "2", label: "Expense" },
    { value: "3", label: "Sponsors" },
  ];

  const handleStateClear = () => {
    if (selectStateRef.current) selectStateRef.current.clearValue();
    if (selectRegionRef.current) selectRegionRef.current.clearValue();
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
  };

  const handleRegionClear = () => {
    if (selectRegionRef.current) selectRegionRef.current.clearValue();
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
  };

  const handleChapterClear = () => {
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
  };

  //excel export start
  const columns = [{ header: "Sl.No.", key: "sno" }];
  columns.push({ header: "Date", key: "date" });
  // if (hasRole([1])) { columns.push({ header: "Country", key: "country_name" }); }
  // if (hasRole([1, 6])) { columns.push({ header: "State", key: "state_name" }); }
  if (hasRole([1, 6, 7])) {
    columns.push({ header: "Region", key: "region_name" });
  }
  if (hasRole([1, 6, 7, 2])) {
    columns.push({ header: "Chapter", key: "chapter_name" });
  }
  columns.push({ header: "Type", key: "type_name" });
  columns.push({ header: "Category", key: "category_name" });
  columns.push({ header: "Created By", key: "creator_name" });
  columns.push({ header: "Members", key: "member_names" });
  columns.push({ header: "Amount", key: "formatted_amount" });

  const { from_date, to_date } = filter;
  let workSheetName = `Income & Expenses Ledger Reports`;
  if (from_date && to_date) {
    if (from_date === to_date) {
      workSheetName += ` (${moment(from_date).format("DD/MM/YYYY")})`;
    } else {
      workSheetName += ` (${moment(from_date).format("DD/MM/YYYY")} to ${moment(to_date).format("DD/MM/YYYY")})`;
    }
  } else {
    workSheetName += ` (Overall)`;
  }

  const title = workSheetName;
  const fileName = "Income & Expenses Ledger Reports";

  const workbook = new Excel.Workbook();

  const exportAsExcel = async () => {
    try {
      const worksheet = workbook.addWorksheet(fileName);
      worksheet.columns = columns;
      worksheet.insertRow(1, []);

      const totalCols = columns.length || 1;
      const lastColLetter = String.fromCharCode(64 + totalCols);
      worksheet.mergeCells(`A1:${lastColLetter}1`);
      const titleCell = worksheet.getCell("A1");
      titleCell.value = title || "Income & Expenses Ledger Reports";
      styleExcelTitleRow(worksheet.getRow(1), totalCols);

      const headerRow = worksheet.getRow(2);
      styleExcelHeaderRow(headerRow, totalCols);

      worksheet.columns.forEach((column) => {
        column.width = column.header.length + 15;
        column.alignment = { horizontal: "center" };
      });

      setBtnExcelDisabled(true);
      await axios
        .get(LEDGER_REPORTS_LIST, {
          params: { type: NOPAGINATE, isAdmin: 1, ...filter },
        })
        .then(async (response) => {
          if (response.status === 200 && response.data.success) {
            const excelData = response.data.data.transactions;

            if (excelData.length < 1) {
              toast.error("No data found!.");
              return;
            }

            excelData.forEach((singleData) => {
              singleData.member_names =
                singleData.members?.length > 0
                  ? singleData.members.map((m) => m.name).join(", ")
                  : "-";
              worksheet.addRow(singleData);
            });

            // Summary Totals
            const {
              formatted_total_income,
              formatted_total_expense,
              formatted_net_balance,
            } = response.data.data;
            const emptyObj = { sno: "" };
            // if (hasRole([1])) emptyObj.country_name = "";
            // if (hasRole([1, 6])) emptyObj.state_name = "";
            if (hasRole([1, 6, 7])) emptyObj.region_name = "";
            if (hasRole([1, 6, 7, 2])) emptyObj.chapter_name = "";
            emptyObj.date = "";
            emptyObj.category_name = "";
            emptyObj.creator_name = "";
            emptyObj.member_names = "";
            emptyObj.formatted_amount = "";
            emptyObj.type_name = "";

            worksheet.addRow({
              ...emptyObj,
              creator_name: "Total Incomes",
              formatted_amount: formatted_total_income,
            });
            worksheet.addRow({
              ...emptyObj,
              creator_name: "Total Expenses",
              formatted_amount: formatted_total_expense,
            });
            worksheet.addRow({
              ...emptyObj,
              creator_name: "Net Balance",
              formatted_amount: formatted_net_balance,
            });

            const highlightRows = [
              worksheet.lastRow.number - 2,
              worksheet.lastRow.number - 1,
              worksheet.lastRow.number,
            ];
            highlightRows.forEach((rowNum, i) => {
              const row = worksheet.getRow(rowNum);
              if (i < 2) {
                styleExcelSummaryRow(row, totalCols);
                return;
              }
              row.eachCell((cell) => {
                cell.font = { bold: true };
                cell.alignment = { horizontal: "center" };
                cell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: i === 2 ? "FFB3E6B3" : EXCEL_SIDEBAR_COLOR },
                };
              });
            });

            worksheet.eachRow({ includeEmpty: false }, (row) => {
              const currentCell = row._cells;
              currentCell.forEach((singleCell) => {
                const cellAddress = singleCell._address;
                worksheet.getCell(cellAddress).border = {
                  top: { style: "thin" },
                  left: { style: "thin" },
                  bottom: { style: "thin" },
                  right: { style: "thin" },
                };
              });
            });

            const buf = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buf]), `${fileName}.xlsx`);
          }
        })
        .finally(() => {
          setBtnExcelDisabled(false);
        });
    } catch (error) {
      toast.error(error.message);
    } finally {
      workbook.removeWorksheet(workSheetName);
    }
  };

  const exportAsPDF = async () => {
    try {
      const doc = new jsPDF("landscape");
      const headers = ["Sl.No.", "Date"];
      // if (hasRole([1])) headers.push('Country');
      // if (hasRole([1, 6])) headers.push('State');
      if (hasRole([1, 6, 7])) headers.push("Region");
      if (hasRole([1, 6, 7, 2])) headers.push("Chapter");
      headers.push("Type");
      headers.push("Category");
      headers.push("Created By");
      headers.push("Members");
      headers.push("Amount");

      setBtnPdfDisabled(true);
      await axios
        .get(LEDGER_REPORTS_LIST, {
          params: { type: NOPAGINATE, isAdmin: 1, ...filter },
        })
        .then(async (response) => {
          if (response.status === 200 && response.data.success) {
            const dataArr = response.data.data.transactions;
            if (dataArr.length < 1) {
              toast.error("No data found!.");
              return;
            }

            const pdfData = dataArr.map((data) => {
              let pData = [data.sno, data.date];
              // if (hasRole([1])) pData.push(data.country_name);
              // if (hasRole([1, 6])) pData.push(data.state_name);
              if (hasRole([1, 6, 7])) pData.push(data.region_name);
              if (hasRole([1, 6, 7, 2])) pData.push(data.chapter_name);
              pData.push(data.type_name);
              pData.push(data.category_name);
              pData.push(data.creator_name);
              pData.push(
                data.members?.length > 0
                  ? data.members.map((m) => m.name).join(", ")
                  : "-",
              );
              pData.push(data.amount);
              return pData;
            });

            const { total_income, total_expense, net_balance } =
              response.data.data;
            const emptyCols = Array(headers.length - 2).fill("");
            pdfData.push([...emptyCols, "Total Income:", total_income]);
            pdfData.push([...emptyCols, "Total Expense:", total_expense]);
            pdfData.push([...emptyCols, "Net Balance:", net_balance]);

            let pdfTitle = title;
            const textWidth =
              (doc.getStringUnitWidth(pdfTitle) * doc.internal.getFontSize()) /
              doc.internal.scaleFactor;
            const textOffset = (doc.internal.pageSize.width - textWidth) / 2;
            doc.text(pdfTitle, textOffset, 10);

            doc.autoTable({
              head: [headers],
              body: pdfData,
              margin: { top: 20, right: 20, bottom: 20, left: 20 },
              startY: 20,
              headerStyles: {
                ...pdfHeaderStyles,
                textColor: [255, 255, 255],
              },
              styles: { lineWidth: 0.1 },
            });
            doc.save(fileName);
          }
        })
        .finally(() => {
          setBtnPdfDisabled(false);
        });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDateRangeClear = () => {
    setShowClearBtn(false);
    if (refFlatpickr.current && refFlatpickr.current.flatpickr) {
      refFlatpickr.current.flatpickr.setDate([]);
    }
    applyFilter({
      ...filter,
      from_date: "",
      to_date: "",
    });
  };

  useEffect(() => {
    setLoading(true);
    axios
      .get(LEDGER_REPORTS_LIST, {
        params: {
          type: PAGINATE,
          page: page,
          perPage: perPage,
          isAdmin: 1,
          ...filter,
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          setData(response.data.data.transactions);
          setSummary({
            total_income: response.data.data.total_income,
            total_expense: response.data.data.total_expense,
            net_balance: response.data.data.net_balance,
          });
          setPaginator({
            prev: response.data.data.prev != null,
            next: response.data.data.next != null,
            total: response.data.data.total,
            current: response.data.data.current_page,
            last: response.data.data.last_page,
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });

    const fetchData = async () => {
      try {
        const countryResponse = await axios.get(COUNTRY_LIST_DROPDOWN);
        setCountryOptions(countryResponse.data.data);

        const stateResponse = await axios.get(STATE_LIST_DROPDOWN, {
          params: { ...filter },
        });
        setStateOptions(stateResponse.data.data);

        const regionResponse = await axios.get(REGION_LIST_DROPDOWN, {
          params: { ...filter },
        });
        setRegionOptions(regionResponse.data.data);

        const chapterResponse = await axios.get(CHAPTER_LIST_DROPDOWN, {
          params: { ...filter },
        });
        setChapterOptions(chapterResponse.data.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [page, filter, perPage]);

  const handleCalendarClick = () => {
    if (refFlatpickr.current) {
      refFlatpickr.current.flatpickr.open();
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-3">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Income & Expenses Ledger Reports</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span>Reports</span>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">
                    Income & Expenses Ledger Reports List
                  </span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                {hasPermission("reports-ledger-list-export") && (
                  <>
                    <button
                      disabled={btnExcelDisabled}
                      type="button"
                      onClick={exportAsExcel}
                      className="btn rounded-pill btn-primary excel-btn ibutton"
                    >
                      <span className="tf-icons bx bxs-spreadsheet"></span>
                      &nbsp; Excel
                    </button>
                    <button
                      disabled={btnPdfDisabled}
                      type="button"
                      onClick={exportAsPDF}
                      className="btn rounded-pill btn-primary pdf-btn ibutton"
                    >
                      <span className="tf-icons bx bxs-file-pdf"></span>&nbsp;
                      Download PDF
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1">
        <div className="card icard sticky-table-card">
          <div className="container-fluid f_containter">
            <div className="row mb-2 gx-3">
              {hasRole([1]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="country_id">
                    Country
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    name="country_id"
                    isClearable
                    options={countryOptions}
                    onChange={(selected) => {
                      handleStateClear();
                      applyFilter({
                        ...filter,
                        country_id: selected?.value || "",
                        state_id: null,
                        region_id: null,
                        chapter_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="state_id">
                    State
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    ref={selectStateRef}
                    name="state_id"
                    isClearable
                    options={stateOptions}
                    onChange={(selected) => {
                      handleRegionClear();
                      applyFilter({
                        ...filter,
                        state_id: selected?.value || "",
                        region_id: null,
                        chapter_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6, 7]) && (
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="region_id">
                      Region
                    </label>
                    <Select
                      ref={selectRegionRef}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      name="region_id"
                      isClearable
                      options={regionOptions}
                      onChange={(selected) => {
                        handleChapterClear();
                        applyFilter({
                          ...filter,
                          region_id: selected?.value || "",
                          chapter_id: null,
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              {hasRole([1, 6, 7, 2]) && (
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="chapter_id">
                      Chapter
                    </label>
                    <Select
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      ref={selectChapterRef}
                      name="chapter_id"
                      isClearable
                      options={chapterOptions}
                      onChange={(selected) =>
                        applyFilter({
                          ...filter,
                          chapter_id: selected?.value || "",
                        })
                      }
                    />
                  </div>
                </div>
              )}

              <div className="col-md-3">
                <div className="mb-3">
                  <label className="form-label">Type</label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    name="transaction_type"
                    isClearable
                    options={transactionTypeOptions}
                    onChange={(selected) =>
                      applyFilter({
                        ...filter,
                        transaction_type: selected?.value || "",
                      })
                    }
                    placeholder="All Types"
                  />
                </div>
              </div>

              <div className="col-md-3">
                <div className="mb-3 dateRangeDiv">
                  <label className="form-label" htmlFor="date_range">
                    Date Range
                  </label>
                  <div className="datepicker_wrapper">
                    <div className="datepicker">
                      <Flatpickr
                        ref={refFlatpickr}
                        className="form-control"
                        options={{
                          mode: "range",
                          dateFormat: "Y-m-d",
                          onChange: (selected) => {
                            setShowClearBtn(true);
                            if (
                              selected[0] !== undefined &&
                              selected[1] !== undefined &&
                              selected[1] !== ""
                            ) {
                              applyFilter({
                                ...filter,
                                from_date: selected[0]
                                  ? moment(selected[0]).format("YYYY-MM-DD")
                                  : "",
                                to_date: selected[1]
                                  ? moment(selected[1]).format("YYYY-MM-DD")
                                  : "",
                              });
                            }
                          },
                          onClose: (selected) => {
                            if (!selected[1]) {
                              setShowClearBtn(false);
                            }
                          },
                        }}
                        placeholder="Date Range"
                      />
                      {showClearBtn && (
                        <button
                          className="border-0 clearDateRangeBtn"
                          data-bs-toggle="tooltip"
                          title="Clear"
                          onClick={handleDateRangeClear}
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      )}
                    </div>
                    <div className="cal_icon" onClick={handleCalendarClick}>
                      <span className="input-group-text calendar-icon"></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!loading && (
              <div className="row mb-3 mt-2">
                <div className="col-md-4">
                  <div className="card bg-success text-white py-2 px-3">
                    <h6 className="m-0 text-white">Total Incomes:</h6>
                    <h4 className="m-0 text-white text-end">
                      {summary.total_income}
                    </h4>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card bg-danger text-white py-2 px-3">
                    <h6 className="m-0 text-white">Total Expenses:</h6>
                    <h4 className="m-0 text-white text-end">
                      {summary.total_expense}
                    </h4>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card bg-primary text-white py-2 px-3">
                    <h6 className="m-0 text-white">Net Balance:</h6>
                    <h4 className="m-0 text-white text-end">
                      {summary.net_balance}
                    </h4>
                  </div>
                </div>
              </div>
            )}
          </div>
          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
          <StickyTable refreshDeps={[data, loading]} scrollClassName="text-nowrap mt-3"><table className="table table-striped itable">
              <thead>
                <tr>
                  <th>Sl. No.</th>
                  <th>Date</th>
                  {hasRole([1]) && <th>Country</th>}
                  {hasRole([1, 6]) && <th>State</th>}
                  {hasRole([1, 6, 7]) && <th>Region</th>}
                  {hasRole([1, 6, 7, 2]) && <th>Chapter</th>}
                  <th>Type</th>
                  <th>Category</th>
                  <th>Created By</th>
                  <th>Members</th>
                  <th>Amount</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody className="table-border-bottom-0">
                {loading ? (
                  <Loader loading={loading} colspan="12" />
                ) : data.length > 0 ? (
                  data.map((item, i) => (
                    <tr key={i}>
                      <td>{item?.sno}</td>
                      <td>{item?.date}</td>
                      {hasRole([1]) && <td>{item?.country_name}</td>}
                      {hasRole([1, 6]) && <td>{item?.state_name || "Tamil Nadu"}</td>}
                      {hasRole([1, 6, 7]) && <td>{item?.region_name}</td>}
                      {hasRole([1, 6, 7, 2]) && <td>{item?.chapter_name}</td>}
                      <td>
                        <span
                          className={`badge ${item?.transaction_type === 1 || item?.transaction_type === 3 ? "bg-success" : "bg-danger"}`}
                        >
                          {item?.type_name}
                        </span>
                      </td>
                      <td>{item?.category_name}</td>
                      <td>{item?.creator_name}</td>
                      <td
                        style={{
                          maxWidth: "250px",
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                        }}
                      >
                        {item?.members?.length > 0
                          ? item.members.map((m) => m.name).join(", ")
                          : "-"}
                      </td>
                      <td
                        className={`fw-bold ${item?.transaction_type === 1 || item?.transaction_type === 3 ? "text-success-dark" : "text-danger"}`}
                      >
                        {item?.formatted_amount}
                      </td>
                      <td>{checkEmptyOrNull(item?.notes)}</td>
                    </tr>
                  ))
                ) : (
                  <NoDataFound
                    colspan="12"
                    message="No Ledger Transactions Found"
                  />
                )}
              </tbody>
            </table>
          </StickyTable>
          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LedgerReportsList;
